import axios from 'axios';
import { appUrl } from '../../../../../utils/config';
import { TONES, ISSUE_TYPES, PROCESS_FLOW_ISSUES } from './poIssuesData';

/**
 * PO Alerts Dashboard — /fetchAllPOAlerts client.
 *
 * Maps the middleware contract (FetchAllPOAlerts_API_Documentation) onto the row
 * shape the table already renders, and sends the Issue / Product / CMO / PO
 * Status selections as the documented server-side filters.
 *
 * alertStatus is deliberately NOT sent: the Open and Closed tab labels show both
 * counts at once, so both sets are needed on every load and filtering by status
 * server-side would cost a second request.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad = (n) => String(n).padStart(2, '0');

// "2026-08-29T08:15:11.405Z" -> { date: "29-Aug-2026", time: "08:15:11" }
export const toStamp = (value) => {
  if (!value) return { date: '', time: '' };
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return { date: String(value), time: '' };
  return {
    date: `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
};

// The API sends sentence case ("Component mismatch"); the table and the Issue
// filter both match against the deck's title-case labels.
const toIssueLabel = (alertName) => {
  const raw = String(alertName == null ? '' : alertName).trim();
  return ISSUE_TYPES.find((t) => t.toLowerCase() === raw.toLowerCase()) || raw;
};

const isClosed = (row) =>
  String(row == null ? '' : row).trim().toLowerCase() === 'closed';

/* ---- "More Information" ----
   alertDetails arrives either as a JSON object of the values that disagree
   (e.g. {"poPlant":"PATC","mdPlant":""}) or as a plain sentence. Objects become
   the deck's labelled chips — PO-side values red, master-data-side green,
   context grey — and a sentence renders as prose with no chip. */

/* Chip label and tone per alertDetails key, keyed lower-case so the middleware's
   casing does not have to match (it writes poSloc where the deck reads PO SLOC).
   Keys are the ones createPoAlerts.js actually writes:
     Component mismatch  { missingInMaterialMapping, missingInBOM }
     Plant mismatch      { poPlant, mdPlant }
     SLoc missing        { plant, poSloc, mdSloc }
     Delivery mismatch   { pickupLocation, deliveryLocation }
   The two component-mismatch keys are the PO and MD chips in the deck: a
   component missing from material mapping is one the PO carries and master data
   does not, and vice versa. */
const INFO_FIELDS = {
  po: { tag: 'PO', tone: TONES.po },
  md: { tag: 'MD', tone: TONES.md },
  missinginmaterialmapping: { tag: 'PO', tone: TONES.po },
  missinginbom: { tag: 'MD', tone: TONES.md },
  poplant: { tag: 'PO PLANT', tone: TONES.po },
  mdplant: { tag: 'MD PLANT', tone: TONES.md },
  posloc: { tag: 'PO SLOC', tone: TONES.po },
  mdsloc: { tag: 'MD SLOC', tone: TONES.md },
  plant: { tag: 'PLANT', tone: TONES.neutral },
  pickuplocation: { tag: 'PICKUP LOCATION', tone: TONES.neutral },
  // Always red. Both deck screenshots show it red, including the Closed-tab row
  // where pickup and delivery are the same value.
  deliverylocation: { tag: 'DELIVERY LOCATION', tone: TONES.po },
};

const fieldOf = (key) => INFO_FIELDS[String(key).toLowerCase()];

const infoLabel = (key) =>
  fieldOf(key)?.tag ||
  String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toUpperCase();

// An unmapped key still gets a side from its po/md prefix rather than silently
// reading grey — that fallback is what made Component Mismatch colourless.
const infoTone = (key) => {
  const mapped = fieldOf(key);
  if (mapped) return mapped.tone;
  const k = String(key).toLowerCase();
  if (k.startsWith('po')) return TONES.po;
  if (k.startsWith('md')) return TONES.md;
  return TONES.neutral;
};

// Components come through as [{ code, name }]; render them as "code · name".
const infoValue = (value) => {
  if (value == null) return '';
  const one = (v) =>
    v && typeof v === 'object'
      ? [v.code, v.name].filter(Boolean).join(' · ') || JSON.stringify(v)
      : String(v);
  if (Array.isArray(value)) return value.map(one).filter(Boolean).join(', ');
  return one(value);
};

export const buildInfo = (alertDetails) => {
  const raw = String(alertDetails == null ? '' : alertDetails).trim();
  if (!raw) return [];

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    parsed = undefined;
  }

  if (parsed === undefined || parsed === null || typeof parsed !== 'object') {
    return [{ tag: '', tone: TONES.neutral, value: raw }];
  }

  if (Array.isArray(parsed)) {
    const value = infoValue(parsed);
    return value ? [{ tag: '', tone: TONES.neutral, value }] : [];
  }

  return Object.entries(parsed).map(([key, value]) => ({
    tag: infoLabel(key),
    tone: infoTone(key),
    value: infoValue(value) || '—',
  }));
};

export const mapPOAlert = (row) => {
  const issue = toIssueLabel(row?.alertName);
  const vendorName = String(row?.vendorName == null ? '' : row.vendorName).trim();
  const alertStatus = String(row?.alertStatus == null ? '' : row.alertStatus).trim();
  const poNumber = row?.poNumber == null ? '' : String(row.poNumber);

  const mapped = {
    // The controller's SELECT does not return the po_alerts_log id, so fall back
    // to the columns that do identify a row. Without a distinct id React reuses
    // one key for every row and "Mark as Resolved" would move all of them.
    id: row?.id || `${poNumber}|${row?.alertName || ''}|${row?.creationDate || ''}`,
    poNumber,
    issue,
    info: buildInfo(row?.alertDetails),
    showProcessFlowLink: PROCESS_FLOW_ISSUES.includes(issue),
    // The API decides what is resolvable (alertName === 'Missing process flow
    // config' && status Open). Only fall back to deriving it if the field is
    // absent, i.e. against a middleware from before that was added.
    showMarkAsResolved:
      typeof row?.showMarkAsResolved === 'boolean'
        ? row.showMarkAsResolved
        : PROCESS_FLOW_ISSUES.includes(issue) && !isClosed(alertStatus),
    cmo: vendorName || (row?.vendorOrgID == null ? '' : String(row.vendorOrgID)),
    productCode: row?.productCode || '',
    productDescription: row?.productDescription || '',
    poStatus: row?.poStatusText || '',
    timestamp: toStamp(row?.creationDate),
    alertStatus,
    // The resolve call needs the raw alertName, plus these three, as its
    // composite key. vendorOrgID / poStatusID also back the filter dropdowns.
    alertName: row?.alertName || '',
    // A PO whose schedule line is marked for delete in the ledger. Not a PO
    // status — it comes from schedule_lines."markedForDelete" — so the table
    // flags it separately from the status pill.
    isMarkedForDelete: row?.isMarkedForDelete === true,
    isDeliveryComplete: row?.isDeliveryComplete === true,
    vendorOrgID: row?.vendorOrgID,
    poStatusID: row?.poStatusID,
  };

  if (isClosed(alertStatus)) {
    mapped.resolvedBy = {
      name: row?.lastUpdatedBy || '',
      ...toStamp(row?.lastUpdatedDate),
    };
  }

  return mapped;
};

const PRODUCT_SEARCH_MAX = 255;

const positiveInts = (values) => [
  ...new Set(
    (Array.isArray(values) ? values : [])
      .map((v) => Number(v))
      .filter((n) => Number.isInteger(n) && n > 0)
  ),
];

/**
 * Build the documented request body. Every field is optional and an empty one is
 * omitted entirely so the API does not filter on it.
 *
 * `issues` are sent as the FE's title-case labels — the controller matches
 * alertsList case-insensitively, so no re-casing is needed.
 */
export const buildPOAlertsRequest = ({
  issues = [],
  productSearch = '',
  poNumberSearch = '',
  vendorIDs = [],
  statusIDs = [],
  includeCancelled = false,
} = {}) => {
  const body = {};

  if (issues.length) body.alertsList = [...issues];

  const search = String(productSearch || '').trim();
  if (search) body.productSearchString = search.slice(0, PRODUCT_SEARCH_MAX);

  // Same contract as the product search — the API trims and caps at 255 too.
  const poSearch = String(poNumberSearch || '').trim();
  if (poSearch) body.poNumberSearchString = poSearch.slice(0, PRODUCT_SEARCH_MAX);

  const vendors = positiveInts(vendorIDs);
  if (vendors.length) body.vendorList = vendors;

  const statuses = positiveInts(statusIDs);
  if (statuses.length) body.statusList = statuses;

  /*
   * Cancelled has no status id — it is the schedule line's markedForDelete flag
   * — so it travels as its own boolean and the API ORs it with statusList. Sent
   * only when true, keeping every other request body byte-for-byte unchanged.
   */
  if (includeCancelled === true) body.includeCancelled = true;

  return body;
};

const FETCH_FAILED = 'Unable to fetch PO alerts.';

// The controller answers 400/500 with `errorMessage`, while the published
// contract documents `message`. Read both so the validation reason (e.g.
// "Invalid alert name: X") actually reaches the user instead of a generic line.
const serverMessage = (data) =>
  String(data?.errorMessage || data?.message || '').trim();

const buildUrl = (endpoint) =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/${endpoint}`;

const authHeaders = () => ({
  headers: { authtoken: localStorage.getItem('authToken') },
});

/**
 * CMO filter options — the network's CMOs, not just the ones that happen to have
 * an alert. `organization_id` is the same id space as the alert's vendorOrgID
 * (both come from the org directory), so it is what vendorList must carry.
 * ecosystem_id is taken from the authtoken, so no request body is needed.
 */
export const fetchCMOOptions = async () => {
  const response = await axios.post(
    buildUrl('fetchCMOMembersDirect'),
    {},
    authHeaders()
  );

  const list = response?.data?.CMOMembersWithId;
  return (Array.isArray(list) ? list : [])
    .map((c) => ({
      name: String(c?.organization_name || c?.organizationName || '').trim(),
      id: Number(c?.organization_id ?? c?.organizationId),
    }))
    .filter((c) => c.name && Number.isInteger(c.id) && c.id > 0);
};

/**
 * PO Status filter options from the status master.
 *
 * Note the id caveat: po_alerts_log."poStatusID" is written from the SAP
 * schedule-line status, so a master id is not guaranteed to match it. The
 * caller therefore prefers an id actually seen in the alert rows and only
 * falls back to statusID here — see buildStatusOptions in the dashboard.
 */
export const fetchPOStatusOptions = async () => {
  const response = await axios.get(buildUrl('fetchAllPOStatuses'), authHeaders());

  const list = response?.data?.data?.poStatuses;
  return (Array.isArray(list) ? list : [])
    .map((s) => ({
      text: String(s?.statusText || '').trim(),
      id: Number(s?.statusID),
    }))
    .filter((s) => s.text && Number.isInteger(s.id) && s.id > 0);
};

/**
 * Mark one alert resolved — PATCH /markPOAlertAsResolved.
 *
 * The alert is addressed by the composite key the API expects: poNumber,
 * productCode, vendorOrgID and alertName. lastUpdatedBy is NOT sent; the
 * middleware stamps it from the authenticated user (request.fullName).
 *
 * Resolves to the updated row so the caller can render the real "Resolved by"
 * stamp. On failure it throws an Error carrying `status`, so a 404 (someone else
 * already resolved it) can be handled differently from a real error.
 */
export const markPOAlertAsResolved = async (row) => {
  let response;
  try {
    response = await axios.patch(
      buildUrl('markPOAlertAsResolved'),
      {
        poNumber: row?.poNumber,
        productCode: row?.productCode,
        vendorOrgID: Number(row?.vendorOrgID),
        alertName: row?.alertName || row?.issue,
      },
      authHeaders()
    );
  } catch (err) {
    const error = new Error(
      serverMessage(err?.response?.data) || 'Unable to mark PO alert as resolved.'
    );
    error.status = err?.response?.status;
    throw error;
  }

  const data = response?.data;
  if (data?.status === false) {
    const error = new Error(
      serverMessage(data) || 'Unable to mark PO alert as resolved.'
    );
    error.status = response?.status;
    throw error;
  }

  return data?.poAlert || null;
};

/**
 * Number of open alerts, for the sidebar badge. Counted in SQL by
 * /fetchOpenPOAlertsCount — it used to call /fetchAllPOAlerts and take the
 * array length, which fetched every open row to produce one number.
 *
 * Returns 0 rather than throwing — a badge is never important enough to break
 * the sidebar — but logs first, because a silent 0 is indistinguishable from
 * "no alerts" and that made a broken badge hard to spot.
 */
export const fetchOpenPOAlertsCount = async () => {
  try {
    const response = await axios.post(
      buildUrl('fetchOpenPOAlertsCount'),
      {},
      authHeaders()
    );
    const data = response?.data;
    if (data?.status === false) return 0;
    return Number.isFinite(data?.openCount) ? data.openCount : 0;
  } catch (err) {
    console.warn('PO alerts count unavailable:', err);
    return 0;
  }
};

export const fetchAllPOAlerts = async (filters) => {
  let response;
  try {
    response = await axios.post(
      buildUrl('fetchAllPOAlerts'),
      buildPOAlertsRequest(filters),
      authHeaders()
    );
  } catch (err) {
    // A 400 or 500 rejects the promise, so the body is on err.response.
    throw new Error(serverMessage(err?.response?.data) || FETCH_FAILED);
  }

  const data = response?.data;
  if (data?.status === false) {
    throw new Error(serverMessage(data) || FETCH_FAILED);
  }

  const mapped = (Array.isArray(data?.poAlerts) ? data.poAlerts : []).map(mapPOAlert);

  return {
    open: mapped.filter((r) => !isClosed(r.alertStatus)),
    closed: mapped.filter((r) => isClosed(r.alertStatus)),
  };
};
